/**
 * Chatbot client-side history.
 *
 * Stored in localStorage and pruned to messages no older than 7 days.
 * Session token (multi-turn clarification id) lives alongside the
 * messages so reloading the page doesn't drop the operator mid-flow.
 *
 * Exposes a tiny imperative API (no React state) — the consuming
 * component (HomeChatbot) keeps its own React state and calls these
 * helpers to persist + replay across reloads.
 */

const STORAGE_KEY = 'chatbot_history_v1';
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_MESSAGES = 100;

export type StoredMessage = {
  id: string;
  role: 'user' | 'bot';
  text: string;
  ts: number;
  card?: { title: string; meta: string; icon?: string };
  options?: string[];
};

export type StoredHistory = {
  messages: StoredMessage[];
  sessionToken: string | null;
  lastClean: number;
};

const empty = (): StoredHistory => ({
  messages: [],
  sessionToken: null,
  lastClean: Date.now(),
});

/** Read + auto-prune. Stale (>7d) messages are dropped on every read so
 *  the cache can't grow forever. Corrupted JSON resets to empty. */
export const loadHistory = (): StoredHistory => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as StoredHistory;
    if (!parsed || !Array.isArray(parsed.messages)) return empty();
    const now = Date.now();
    const fresh = parsed.messages.filter((m) => m && typeof m.ts === 'number' && (now - m.ts) < MAX_AGE_MS);
    const trimmed = fresh.slice(-MAX_MESSAGES);
    return {
      messages: trimmed,
      sessionToken: parsed.sessionToken || null,
      lastClean: now,
    };
  } catch {
    return empty();
  }
};

export const saveHistory = (h: StoredHistory): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(h));
  } catch {
    // localStorage may be full or unavailable (private mode). Drop silently.
  }
};

export const appendMessage = (h: StoredHistory, msg: Omit<StoredMessage, 'ts'> & { ts?: number }): StoredHistory => {
  const stamped: StoredMessage = { ...msg, ts: msg.ts ?? Date.now() };
  const messages = [...h.messages, stamped].slice(-MAX_MESSAGES);
  return { ...h, messages };
};

export const clearHistory = (): StoredHistory => {
  const e = empty();
  saveHistory(e);
  return e;
};

/** Map stored messages to the wire shape the BE expects (role + text only). */
export const toApiHistory = (h: StoredHistory) =>
  h.messages.map((m) => ({ role: m.role, text: m.text }));
